<template>
  <!--
    The card IS the radio — Reka's RadioGroupItem with the circle traded for a
    panel, so the group keeps one tab stop, arrow keys, and `aria-checked`
    without any of it being hand-rolled. Pair it with DSRadioGroup.
  -->
  <RadioGroupItem
    :value
    :disabled
    :class="cn(
      'group/choice flex w-full items-start gap-3 p-3.5 text-left cursor-pointer',
      'rounded-ds-md border-[1.5px] bg-card border-border',
      'transition-[background-color,border-color,box-shadow] duration-ds-fast ease-ds-standard',
      'hover:border-strong/35',
      'data-[state=checked]:border-primary data-[state=checked]:bg-accent',
      'data-[state=checked]:shadow-ds-xs data-[state=checked]:hover:border-primary',
      'focus-visible:outline-none focus-visible:shadow-ds-focus',
      'disabled:cursor-not-allowed disabled:opacity-50',
      props.class,
    )"
  >
    <span
      v-if="icon"
      class="inline-flex items-center justify-center size-8 shrink-0 rounded-ds-sm
        bg-secondary text-muted-foreground
        transition-colors duration-ds-fast ease-ds-standard
        group-data-[state=checked]/choice:bg-primary
        group-data-[state=checked]/choice:text-primary-foreground"
    >
      <component
        :is="icon"
        class="size-4"
      />
    </span>

    <span class="flex-1 min-w-0">
      <span class="flex items-center gap-1.5">
        <span class="text-sm font-bold text-foreground group-data-[state=checked]/choice:text-accent-foreground">
          <slot>{{ title }}</slot>
        </span>
        <!--
          The tick is redundant to a screen reader — the item already reports
          aria-checked — so it is decorative and hidden from one.
        -->
        <Check
          class="size-3.5 shrink-0 text-primary opacity-0 group-data-[state=checked]/choice:opacity-100"
          aria-hidden="true"
        />
      </span>
      <span
        v-if="description"
        class="block mt-0.5 text-xs font-medium leading-normal
          text-muted-foreground group-data-[state=checked]/choice:text-accent-foreground/85"
      >
        {{ description }}
      </span>
    </span>
  </RadioGroupItem>
</template>

<script setup lang="ts">
import type { Component, HTMLAttributes } from 'vue'
import { Check } from '@lucide/vue'
import { RadioGroupItem } from 'reka-ui'
import { cn } from '@ds/lib/utils'

/*
 * One option in a two-or-three way choice, drawn as a panel rather than a dot:
 * an icon, what it is, and one line on what picking it means.
 *
 * A pattern, not a primitive — it is RadioGroupItem plus a layout the canonical
 * Radio.jsx does not describe, so its metrics are (derived) from Card.jsx and
 * the spec's own drawing.
 *
 * Straight from Reka rather than through DSRadioGroupItem: that one draws the
 * canonical 20px dot and renders no slot, so composing it here produced a 20px
 * square with the card's content dropped on the floor. A pattern may reach for
 * Reka directly — DSDateField does the same.
 */
const props = defineProps<{
  /** The value this card commits to the group's `v-model`. */
  value: string
  title?: string
  /** One line on the consequence of picking it. Optional, but rarely skippable. */
  description?: string
  icon?: Component
  disabled?: boolean
  class?: HTMLAttributes['class']
}>()
</script>
